import { ruleFor, eurRate, nlDate, useIsdeRules } from "../../lib/isde";

const th = "p-3 pr-4 text-left align-top sm:p-4";

export const Badge = ({ status, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold ring-1 ${status === "actief" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>
    {status === "actief" ? "Geldende regeling" : "Voorgenomen – nog niet definitief"}{label ? ` · ${label}` : ""}
  </span>
);

const RateTable = ({ rule }) => (
  <div className="card overflow-hidden" data-testid={`isde-rates-${rule.year}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
      <p className="font-semibold text-ink">{rule.name}</p>
      <Badge status={rule.status} />
    </div>
    <table className="w-full text-[14px] sm:text-[15px]">
      <caption className="sr-only">ISDE-bedragen {rule.year}</caption>
      <thead><tr className="text-[12px] uppercase tracking-[0.1em] text-slate-500"><th scope="col" className={th}>Maatregel</th><th scope="col" className={`${th} hidden sm:table-cell`}>Voorwaarde</th><th scope="col" className={`${th} text-right`}>Per m²</th></tr></thead>
      <tbody>
        {rule.rates.map((r) => (
          <tr key={r.key} className="border-t border-slate-200">
            <th scope="row" className={`${th} font-medium text-ink`}>{r.name}<span className="mt-0.5 block text-[12.5px] font-normal text-slate-500 sm:hidden">{r.condition}</span></th>
            <td className={`${th} hidden text-slate-600 sm:table-cell`}>{r.condition}</td>
            <td className={`${th} text-right font-semibold text-ink`}>{eurRate(r.amount_per_m2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {(rule.transitions?.length > 0 || rule.public_note) && (
      <div className="space-y-1.5 border-t border-slate-200 px-4 py-3 text-[13px] leading-relaxed text-slate-600">
        {rule.transitions.map((t) => <p key={t.key}><strong className="text-ink">Overgangsregeling:</strong> {t.name} gekocht vóór of op {nlDate(t.purchased_until)} en in {rule.year} geplaatst: {eurRate(t.amount_per_m2)} per m². Bewijs: {t.proof}.</p>)}
        {rule.public_note && <p>{rule.public_note}</p>}
      </div>
    )}
  </div>
);

export const IsdeRatesTable = () => {
  const rules = useIsdeRules();
  if (!rules) return <div className="h-48 animate-pulse rounded-2xl bg-slate-100" aria-hidden="true" />;
  const cur = ruleFor(rules, "actief");
  const today = new Date().toISOString().slice(0, 10);
  const future = rules.filter((r) => r.valid_from > today);
  const past = rules.filter((r) => r.status === "actief" && r.valid_to && r.valid_to < today).sort((a, b) => b.year - a.year);
  return (
    <div className="space-y-5" data-testid="isde-rates">
      {cur ? <RateTable rule={cur} /> : <p className="text-slate-600">De bedragen voor dit jaar worden momenteel gecontroleerd.</p>}
      {future.map((r) => <RateTable key={r.id} rule={r} />)}
      {past.length > 0 && (
        <details className="rounded-2xl border border-slate-200 p-4" data-testid="isde-rates-history">
          <summary className="cursor-pointer font-semibold text-ink">Eerdere regeljaren (voor werkzaamheden die al zijn uitgevoerd)</summary>
          <div className="mt-4 space-y-4">{past.map((r) => <RateTable key={r.id} rule={r} />)}</div>
        </details>
      )}
      {cur?.last_checked && <p className="text-[13px] text-slate-500" data-testid="isde-rates-checked">Bedragen gecontroleerd bij RVO op {nlDate(cur.last_checked)}. Bron: <a className="text-maas underline underline-offset-2" href={cur.sources[0]?.url} target="_blank" rel="noopener noreferrer">{cur.sources[0]?.label}</a>.</p>}
    </div>
  );
};

export const KeyValueTable = ({ rows, head, testid, caption }) => (
  <div className="card overflow-hidden" data-testid={testid}>
    <table className="w-full table-fixed text-left text-[14px] sm:text-[15px]">
      <caption className="sr-only">{caption}</caption>
      {head && <thead className="bg-slate-50"><tr>{head.map((h, i) => <th key={h} scope="col" className={`${th} ${i === 0 ? "w-[34%] text-[12px] uppercase tracking-[0.1em] text-slate-500" : "font-display font-bold text-ink"}`}>{h}</th>)}</tr></thead>}
      <tbody>
        {rows.map(([k, ...vals]) => (
          <tr key={k} className="border-t border-slate-200 first:border-0">
            <th scope="row" className={`${th} ${head ? "" : "w-[36%]"} font-medium text-ink`}>{k}</th>
            {vals.map((v, i) => <td key={i} className={`${th} text-slate-600`}>{v}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
